import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp } from 'lucide-react';
import AdSlot from '../advertisement/AdSlot';
import { SME_TOOLS } from '../sme-ipos/SmeSidebar';

const MAX_TRENDING = 3;

const shortName = (company) => company.replace(/\s+(Ltd\.?|Limited)$/i, '');

// smeIpos: rows from utils/smeIpos.js; only live or upcoming issues with a GMP quote trend here
export const SmeReviewsSidebar = ({ smeIpos, className = '' }) => {
  const trending = smeIpos
    .filter((row) => typeof row.gmpPercent === 'number' && ['open', 'closing_today', 'upcoming'].includes(row.status))
    .sort((a, b) => b.gmpPercent - a.gmpPercent)
    .slice(0, MAX_TRENDING);

  return (
    <aside className={`grid grid-cols-1 md:grid-cols-2 min-[1280px]:grid-cols-1 gap-5 items-start ${className}`} aria-label="SME tools and partners">
      {/* Partner ads (GET /api/ads): dedicated SME review slots first, then the shared ones */}
      <AdSlot slotId={['sme-reviews-sidebar-top', 'incontent']} variant="compact" />

      <section aria-labelledby="trending-sme-gmp-heading" className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 sm:p-5">
        <div className="flex items-center justify-between gap-2">
          <h2 id="trending-sme-gmp-heading" className="flex items-center gap-2 text-sm font-extrabold text-slate-900">
            <TrendingUp className="w-4 h-4 text-[#008080]" aria-hidden="true" />
            Trending SME GMP
          </h2>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
            Live
          </span>
        </div>
        {trending.length ? (
          <ul className="mt-3 divide-y divide-slate-100">
            {trending.map((row) => (
              <li key={row.id} className="py-2.5">
                <Link to={`/ipo-gmp/${row.gmpSlug}`} className="flex items-start justify-between gap-3 group">
                  <span className="min-w-0">
                    <span className="block text-sm font-bold text-slate-800 truncate group-hover:text-[#008080]">{shortName(row.company)}</span>
                    <span className="block text-[11px] text-slate-400">Issue: {row.priceBand || '—'}</span>
                  </span>
                  <span className={`text-right text-sm font-extrabold whitespace-nowrap ${row.gmp >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {row.gmp >= 0 ? '+' : '−'}₹{Math.abs(row.gmp)}
                    <span className="block text-[10px] font-semibold">
                      ({row.gmpPercent >= 0 ? '+' : ''}
                      {row.gmpPercent.toFixed(1)}%)
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-xs text-slate-400">No SME grey market quotes right now. They usually appear a few days before bidding opens.</p>
        )}
        <Link to="/sme-ipo-gmp" className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#008080] hover:text-[#006666]">
          View Complete Grey Market Board
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </section>

      <AdSlot slotId={['sme-reviews-sidebar-bottom', 'infeed']} variant="compact" />

      <section aria-labelledby="sme-review-tools-heading" className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 sm:p-5">
        <h2 id="sme-review-tools-heading" className="text-xs font-extrabold uppercase tracking-wider text-slate-700">
          SME Research Tools
        </h2>
        <ul className="mt-2 divide-y divide-slate-100">
          {SME_TOOLS.filter((tool) => tool.to !== '/sme-ipo-reviews').map(({ label, to, icon: Icon }) => (
            <li key={label}>
              <Link to={to} className="flex items-center gap-2.5 py-2 text-sm font-semibold text-slate-700 hover:text-[#008080]">
                <Icon className="w-4 h-4 text-[#008080]" aria-hidden="true" />
                {label}
              </Link>
            </li>
          ))}
          <li>
            <Link to="/sme-ipos" className="flex items-center gap-2.5 py-2 text-sm font-semibold text-slate-700 hover:text-[#008080]">
              <TrendingUp className="w-4 h-4 text-[#008080]" aria-hidden="true" />
              SME IPO List
            </Link>
          </li>
        </ul>
      </section>
    </aside>
  );
};

export default SmeReviewsSidebar;
